import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { backofficeClient } from "@/lib/api/backoffice-client";

export interface Desk {
  id: string;
  name: string;
  emoji: string | null;
  description?: string | null;
  enabled: boolean;
  employees: { id: string }[];
  template_id?: string | null;
}

export interface DeskTag {
  id: number;
  desk_id: string;
  name: string;
  color: string | null;
  description: string | null;
}

interface DeskTagsResponse {
  items: DeskTag[];
  total_count: number;
}

interface DeskEmployeesResponse {
  employee_ids: string[];
}

interface DesksListResponse {
  desks: Desk[];
}

export interface Subdesk {
  id: string;
  name: string;
  is_default: boolean;
}

interface SubdesksListResponse {
  subdesks: Subdesk[];
}

export interface TicketSummary {
  id: string;
  title: string;
  status: string;
}

interface TicketsListResponse {
  items: TicketSummary[];
  total_count: number;
}

export interface CatalogItemSummary {
  id: string;
  name: string;
  icon: string | null;
  desk_id: string;
  ticket_type: string;
  is_visible: boolean;
}

interface CategoryWithItems {
  id: string;
  name: string;
  items?: CatalogItemSummary[];
}

interface CategoriesListResponse {
  categories: CategoryWithItems[];
  total_count: number;
}

interface BulkTransferDeskResponse {
  total: number;
  succeeded: number;
  failed: number;
}

interface BulkMoveCatalogItemsRequest {
  item_ids: string[];
  target_desk_id: string;
}

interface BulkMoveCatalogItemsResponse {
  total: number;
  succeeded: number;
  failed: number;
}

export function useDesks(tenantId: string) {
  return useQuery({
    queryKey: ["service-desk", "desks", tenantId],
    queryFn: async () => {
      const { data } = await backofficeClient.get<DesksListResponse>(
        `/proxy/service-desk/api/v1/desks/`,
        { params: { tenant_id: tenantId, include_disabled: true } },
      );
      return data.desks;
    },
    enabled: Boolean(tenantId),
  });
}

export interface DeskCustomFieldDefinition {
  id: number;
  desk_id: string | null;
  name: string;
  field_type: string;
  is_internal: boolean;
}

interface DeskCustomFieldsResponse {
  items: DeskCustomFieldDefinition[];
  total_count: number;
}

/** A desk's ticket custom field definitions, tenant-global fields included. */
export function useDeskCustomFields(tenantId: string, deskId: string) {
  return useQuery({
    queryKey: ["service-desk", "custom-fields", tenantId, deskId],
    queryFn: async () => {
      const { data } = await backofficeClient.get<DeskCustomFieldsResponse>(
        `/proxy/service-desk/api/v1/desks/${deskId}/custom-fields/`,
        { params: { tenant_id: tenantId } },
      );
      return data.items;
    },
    enabled: Boolean(tenantId) && Boolean(deskId),
  });
}

export function subdesksQueryKey(tenantId: string, deskId: string) {
  return ["service-desk", "subdesks", tenantId, deskId] as const;
}

/** A desk's subdesks, including its main subdesk. */
export async function fetchSubdesks(tenantId: string, deskId: string): Promise<Subdesk[]> {
  const { data } = await backofficeClient.get<SubdesksListResponse>(
    `/proxy/service-desk/api/v1/desks/${deskId}/subdesks`,
    { params: { tenant_id: tenantId, include_main: true } },
  );
  return data.subdesks;
}

export function useSubdesks(tenantId: string, deskId: string) {
  return useQuery({
    queryKey: subdesksQueryKey(tenantId, deskId),
    queryFn: () => fetchSubdesks(tenantId, deskId),
    enabled: Boolean(tenantId && deskId),
  });
}

export function useTicketCount(tenantId: string, deskId: string) {
  return useQuery({
    queryKey: ["service-desk", "tickets", "count", tenantId, deskId],
    queryFn: async () => {
      const { data } = await backofficeClient.get<TicketsListResponse>(
        `/proxy/service-desk/api/v1/tickets/`,
        { params: { tenant_id: tenantId, desk_id: deskId, page_size: 1, page: 1 } },
      );
      return data.total_count;
    },
    enabled: Boolean(tenantId) && Boolean(deskId),
  });
}

export function useCatalogCategories(tenantId: string) {
  return useQuery({
    queryKey: ["service-catalog", "categories", tenantId],
    queryFn: async () => {
      const { data } = await backofficeClient.get<CategoriesListResponse>(
        `/proxy/service-catalog/api/v1/categories`,
        { params: { tenant_id: tenantId } },
      );
      return data;
    },
    enabled: Boolean(tenantId),
  });
}

export function useDeskTags(tenantId: string, deskId: string) {
  return useQuery({
    queryKey: ["service-desk", "desk-tags", tenantId, deskId],
    queryFn: async () => {
      const { data } = await backofficeClient.get<DeskTagsResponse>(
        `/proxy/service-desk/api/v1/desks/${deskId}/tags`,
        { params: { tenant_id: tenantId } },
      );
      return data.items;
    },
    enabled: Boolean(tenantId) && Boolean(deskId),
  });
}

export function useDeskEmployees(tenantId: string, deskId: string) {
  return useQuery({
    queryKey: ["service-desk", "desk-employees", tenantId, deskId],
    queryFn: async () => {
      const { data } = await backofficeClient.get<DeskEmployeesResponse>(
        `/proxy/service-desk/api/v1/desks/${deskId}/employees`,
        { params: { tenant_id: tenantId } },
      );
      return data.employee_ids;
    },
    enabled: Boolean(tenantId) && Boolean(deskId),
  });
}

export function useCopyDeskEmployees() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ tenantId, targetDeskId, employeeIds }: { tenantId: string; targetDeskId: string; employeeIds: string[] }) => {
      const { data } = await backofficeClient.put<DeskEmployeesResponse>(
        `/proxy/service-desk/api/v1/desks/${targetDeskId}/employees`,
        { employee_ids: employeeIds },
        { params: { tenant_id: tenantId } },
      );
      return data.employee_ids;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["service-desk", "desk-employees"] });
    },
  });
}

export function useCopyDeskTags() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ tenantId, targetDeskId, tags }: { tenantId: string; targetDeskId: string; tags: DeskTag[] }) => {
      const results: { succeeded: number; failed: number } = { succeeded: 0, failed: 0 };
      for (const tag of tags) {
        try {
          await backofficeClient.post(
            `/proxy/service-desk/api/v1/desks/${targetDeskId}/tags`,
            { name: tag.name, color: tag.color, description: tag.description },
            { params: { tenant_id: tenantId } },
          );
          results.succeeded++;
        } catch {
          results.failed++;
        }
      }
      return results;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["service-desk", "desk-tags"] });
    },
  });
}

async function fetchAllTicketIds(tenantId: string, deskId: string): Promise<string[]> {
  const ids: string[] = [];
  let page = 1;
  const pageSize = 100;
  let hasMore = true;

  while (hasMore) {
    const { data } = await backofficeClient.get<TicketsListResponse>(
      `/proxy/service-desk/api/v1/tickets/`,
      { params: { tenant_id: tenantId, desk_id: deskId, page_size: pageSize, page } },
    );
    ids.push(...data.items.map((t) => t.id));
    hasMore = ids.length < data.total_count;
    page++;
  }

  return ids;
}

export function useBulkTransferTickets() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ tenantId, deskId, targetDeskId }: { tenantId: string; deskId: string; targetDeskId: string }) => {
      const allIds = await fetchAllTicketIds(tenantId, deskId);
      if (allIds.length === 0) return { total: 0, succeeded: 0, failed: 0 };

      let totalSucceeded = 0;
      let totalFailed = 0;
      const batchSize = 200;

      for (let i = 0; i < allIds.length; i += batchSize) {
        const batch = allIds.slice(i, i + batchSize);
        const { data } = await backofficeClient.post<BulkTransferDeskResponse>(
          `/proxy/service-desk/api/v1/tickets/bulk-transfer-desk`,
          {
            ticket_ids: batch,
            target_desk_id: targetDeskId,
            transfer_reason: "service_desk_restructure_or_consolidation",
          },
          { params: { tenant_id: tenantId } },
        );
        totalSucceeded += data.succeeded;
        totalFailed += data.failed;
      }

      return { total: allIds.length, succeeded: totalSucceeded, failed: totalFailed };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["service-desk", "tickets"] });
    },
  });
}

export function useBulkMoveCatalogItems() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ tenantId, itemIds, targetDeskId }: { tenantId: string; itemIds: string[]; targetDeskId: string }) => {
      if (itemIds.length === 0) return { total: 0, succeeded: 0, failed: 0 };

      let totalSucceeded = 0;
      let totalFailed = 0;
      const batchSize = 200;

      for (let i = 0; i < itemIds.length; i += batchSize) {
        const batch = itemIds.slice(i, i + batchSize);
        const { data } = await backofficeClient.post<BulkMoveCatalogItemsResponse>(
          `/proxy/service-catalog/api/v1/items/bulk-move`,
          {
            item_ids: batch,
            target_desk_id: targetDeskId,
          } satisfies BulkMoveCatalogItemsRequest,
          { params: { tenant_id: tenantId } },
        );
        totalSucceeded += data.succeeded;
        totalFailed += data.failed;
      }

      return { total: itemIds.length, succeeded: totalSucceeded, failed: totalFailed };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["service-catalog"] });
    },
  });
}

interface CountResponse {
  items: unknown[];
  total_count: number;
}

export function useDeskCannedResponses(tenantId: string, deskId: string) {
  return useQuery({
    queryKey: ["service-desk", "canned-responses", tenantId, deskId],
    queryFn: async () => {
      const { data } = await backofficeClient.get<CountResponse>(
        `/proxy/service-desk/api/v1/desks/${deskId}/canned-responses`,
        { params: { tenant_id: tenantId } },
      );
      return data.total_count;
    },
    enabled: Boolean(tenantId) && Boolean(deskId),
  });
}

export function useDeskAutomationSettings(tenantId: string, deskId: string) {
  return useQuery({
    queryKey: ["service-desk", "automation", tenantId, deskId],
    queryFn: async () => {
      const results: string[] = [];
      try {
        const { data: assignment } = await backofficeClient.get<{ enabled: boolean }>(
          `/proxy/service-desk/api/v1/desks/${deskId}/automation/assignment`,
          { params: { tenant_id: tenantId } },
        );
        if (assignment.enabled) results.push("Auto-assignment");
      } catch { /* not configured */ }
      try {
        const { data: survey } = await backofficeClient.get<{ enabled: boolean }>(
          `/proxy/service-desk/api/v1/desks/${deskId}/automation/survey`,
          { params: { tenant_id: tenantId } },
        );
        if (survey.enabled) results.push("Satisfaction survey");
      } catch { /* not configured */ }
      return results;
    },
    enabled: Boolean(tenantId) && Boolean(deskId),
  });
}

export interface WorkflowWithDeskRef {
  id: string;
  name: string;
  enabled: boolean;
}

interface WorkflowInstanceItem {
  id: string;
  workflow_name: string;
  enabled: boolean;
  config: Record<string, unknown>;
  published_version: number | null;
}

interface WorkflowInstancesResponse {
  items: WorkflowInstanceItem[];
  total_count: number;
}

function containsDeskId(obj: unknown, deskId: string): boolean {
  if (typeof obj === "string") return obj === deskId;
  if (Array.isArray(obj)) return obj.some((item) => containsDeskId(item, deskId));
  if (obj && typeof obj === "object") {
    return Object.values(obj).some((val) => containsDeskId(val, deskId));
  }
  return false;
}

interface VersionDetailResponse {
  definition: Record<string, unknown>;
}

export function useWorkflowsReferencingDesk(tenantId: string, deskId: string) {
  return useQuery({
    queryKey: ["workflows", "desk-refs", tenantId, deskId],
    queryFn: async () => {
      const { data } = await backofficeClient.get<WorkflowInstancesResponse>(
        `/proxy/workflows/api/v2/workflows/instances`,
        { params: { tenant_id: tenantId } },
      );

      const configMatches = data.items.filter((i) => containsDeskId(i.config, deskId));
      const needVersionCheck = data.items.filter(
        (i) => !containsDeskId(i.config, deskId) && i.published_version,
      );

      const results = await Promise.allSettled(
        needVersionCheck.map((instance) =>
          backofficeClient
            .get<VersionDetailResponse>(
              `/proxy/workflows/api/v2/workflows/${instance.id}/versions/${instance.published_version}`,
              { params: { tenant_id: tenantId } },
            )
            .then(({ data: version }) =>
              containsDeskId(version.definition, deskId) ? instance : null,
            ),
        ),
      );

      const versionMatches = results
        .filter((r): r is PromiseFulfilledResult<WorkflowInstanceItem | null> => r.status === "fulfilled")
        .map((r) => r.value)
        .filter(Boolean) as WorkflowInstanceItem[];

      return [...configMatches, ...versionMatches].map((i) => ({
        id: i.id,
        name: i.workflow_name,
        enabled: i.enabled,
      }));
    },
    enabled: Boolean(tenantId) && Boolean(deskId),
    staleTime: 60_000,
  });
}

export function useDeleteDesk() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ tenantId, deskId }: { tenantId: string; deskId: string }) => {
      await backofficeClient.delete(
        `/proxy/service-desk/api/v1/desks/${deskId}`,
        { params: { tenant_id: tenantId } },
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["service-desk", "desks"] });
    },
  });
}
